/* Storeyza shared behaviours (home v2, §B0.4). Section builders only add markup:
   .sz-reveal (+ style="--sz-d:N")           fade/rise on scroll; on screen at load = shown at once; opt-in via html.sz-anim
   button[data-sz-rail="<rail id>"][data-sz-dir="1|-1"][data-sz-step]   scrolls the rail by ±328 px
   [data-sz-tabs="<rail id>"] holding [role=tab][aria-controls]       WAI-ARIA tabs, arrows/Home/End, rail back to 0
   [data-sz-checklist="<storage key>"] + inputs, [data-sz-count="{n} sur {total}"]   live counter, .is-complete, localStorage
   [data-sz-rotate][data-sz-seconds][data-sz-rotate-below] + [data-sz-rotate-item]  one message at a time (class is-on)
   button[data-sz-pause]                     pauses every decorative animation of the page (html.sz-paused, WCAG 2.2.2)
   #menu-drawer                              Dawn's drawer becomes an accordion (class sz-accordion)
   Everything re-initialises on shopify:section:load; intervals stop on shopify:section:unload.
   Theme-editor block selection lives in storeyza-editor.js, loaded only in the editor (request.design_mode).
   Written compactly on purpose: Shopify minifies but does not shorten names (budget §E.1). */
(() => {
  const d = document;
  const h = d.documentElement;
  const rm = matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new WeakMap();
  let timers = [];
  let io;
  const $$ = (r, s) => {
    const l = [...r.querySelectorAll(s)];
    if (r !== d && r.matches && r.matches(s)) l.unshift(r);
    return l;
  };
  /* true the first time a behaviour `k` is bound to `el` */
  const once = (k, el) => {
    const s = seen.get(el) || new Set();
    if (s.has(k)) return false;
    seen.set(el, s.add(k));
    return true;
  };
  const beh = () => (rm.matches ? 'auto' : 'smooth');
  const show = (e) => e.classList.add('is-in');
  const at = (e, a) => e.getAttribute(a);
  const byId = (i) => d.getElementById(i);

  const reveal = (r) => {
    const els = $$(r, '.sz-reveal').filter((e) => !e.classList.contains('is-in'));
    if (!els.length) return;
    if (rm.matches || !window.IntersectionObserver) return els.forEach(show);
    io = io || new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { show(e.target); io.unobserve(e.target); }
    }), { rootMargin: '0px 0px -6% 0px' });
    const vh = innerHeight;
    els.forEach((e) => {
      const b = e.getBoundingClientRect();
      if (b.bottom > 0 && b.top < vh && (b.width || b.height)) show(e);
      else io.observe(e);
    });
    h.classList.add('sz-anim');
  };

  const rails = (r) => $$(r, '[data-sz-rail]').forEach((b) => once('r', b) && b.addEventListener('click', () => {
    const rail = byId(at(b, 'data-sz-rail'));
    if (rail) rail.scrollBy({ left: (+at(b, 'data-sz-dir') || 1) * (+at(b, 'data-sz-step') || 328), behavior: beh() });
  }));

  const tabs = (r) => $$(r, '[data-sz-tabs]').forEach((l) => {
    if (!once('t', l)) return;
    const ts = [...l.querySelectorAll('[role="tab"]')];
    const sel = (t, focus) => {
      ts.forEach((x) => {
        const on = x === t;
        const p = byId(at(x, 'aria-controls'));
        x.setAttribute('aria-selected', on);
        x.tabIndex = on ? 0 : -1;
        if (p) p.hidden = !on;
      });
      const rail = byId(at(l, 'data-sz-tabs'));
      if (rail) rail.scrollTo({ left: 0, behavior: beh() });
      if (focus) t.focus();
      const p = byId(at(t, 'aria-controls'));
      if (p) reveal(p);
    };
    ts.forEach((t, i) => {
      t.addEventListener('click', () => sel(t));
      t.addEventListener('keydown', (e) => {
        const n = { ArrowRight: ts[(i + 1) % ts.length], ArrowLeft: ts[(i - 1 + ts.length) % ts.length], Home: ts[0], End: ts[ts.length - 1] }[e.key];
        if (n) { e.preventDefault(); sel(n, true); }
      });
    });
    l.szSelect = sel;
  });

  const checklist = (r) => $$(r, '[data-sz-checklist]').forEach((nb) => {
    if (!once('c', nb)) return;
    const bx = [...nb.querySelectorAll('input[type="checkbox"]')];
    const out = nb.querySelector('[data-sz-count]');
    const tpl = out && at(out, 'data-sz-count');
    const key = at(nb, 'data-sz-checklist');
    try {
      const s = JSON.parse(localStorage.getItem(key));
      if (s && s.length === bx.length) bx.forEach((b, i) => { b.checked = !!s[i]; });
    } catch (e) { /* storage blocked: default ticks stay */ }
    const count = (save) => {
      const n = bx.filter((b) => b.checked).length;
      if (tpl) out.textContent = tpl.replace('{n}', n).replace('{total}', bx.length);
      nb.classList.toggle('is-complete', n > 0 && n === bx.length);
      if (save) try { localStorage.setItem(key, JSON.stringify(bx.map((b) => +b.checked))); } catch (e) { /* ignore */ }
    };
    bx.forEach((b) => b.addEventListener('change', () => count(true)));
    count();
  });

  const rotate = (r) => $$(r, '[data-sz-rotate]').forEach((bar) => {
    if (!once('a', bar)) return;
    const it = [...bar.querySelectorAll('[data-sz-rotate-item]')];
    let k = Math.max(0, it.findIndex((x) => x.classList.contains('is-on')));
    let paused = false;
    bar.szShow = (el) => {
      const i = it.indexOf(el);
      if (i < 0) return;
      it[k].classList.remove('is-on');
      k = i;
      el.classList.add('is-on');
    };
    if (it.length < 2 || rm.matches) return;
    const below = +at(bar, 'data-sz-rotate-below') || 721;
    ['mouseenter', 'focusin'].forEach((t) => bar.addEventListener(t, () => { paused = true; }));
    ['mouseleave', 'focusout'].forEach((t) => bar.addEventListener(t, () => { paused = false; }));
    timers.push({ el: bar, id: setInterval(() => {
      if (!paused && !bar.szHold && innerWidth < below) bar.szShow(it[(k + 1) % it.length]);
    }, (+at(bar, 'data-sz-seconds') || 5) * 1000) });
  });

  /* One button pauses or resumes every decorative animation (marquee, seal, tag sway) */
  const pause = (r) => $$(r, '[data-sz-pause]').forEach((b) => once('p', b) && b.addEventListener('click', () => {
    const on = h.classList.toggle('sz-paused');
    d.querySelectorAll('[data-sz-pause]').forEach((x) => x.setAttribute('aria-pressed', on));
  }));

  /* Mobile drawer as an accordion (§C.6): level-1 and level-2 summaries open in place instead of Dawn's sliding
     sub-panels. A capture listener stops Dawn's summary handler; the native <details> toggle still happens. Without
     this script the drawer keeps Dawn's sliding behaviour (the accordion CSS only applies under .sz-accordion). */
  const drawer = (r) => $$(r, '#menu-drawer').forEach((dr) => {
    if (!once('d', dr)) return;
    dr.classList.add('sz-accordion');
    dr.addEventListener('click', (e) => {
      const s = e.target.closest && e.target.closest('summary');
      if (s && dr.contains(s)) e.stopPropagation();
    }, true);
    dr.querySelectorAll('details').forEach((det) => det.addEventListener('toggle', () => {
      const s = det.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', det.open);
      /* Opening a level-1 item also opens its groups (Par forme, Pour bébé…), like the desktop panel */
      const top = det.parentElement?.parentElement?.parentElement;
      if (det.open && top && top.classList.contains('menu-drawer__navigation')) {
        det.querySelectorAll('.menu-drawer__submenu details').forEach((x) => { x.open = true; });
      }
    }));
  });

  const init = (r = d) => {
    reveal(r); rails(r); tabs(r); checklist(r); rotate(r); pause(r); drawer(r);
  };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', () => init());
  else init();
  d.addEventListener('shopify:section:load', (e) => init(e.target));
  d.addEventListener('shopify:section:unload', (e) => {
    timers = timers.filter((t) => (e.target.contains(t.el) ? clearInterval(t.id) : true));
  });

  window.Storeyza = { init, show };
})();
